import { addWeeks, subDays, addMonths, startOfDay } from 'date-fns';

import { fCurrencyRupees } from 'src/utils/format-number';

import { LabelColor } from 'src/components/label';

import { AuditType, AuditAction, IAuditListItem } from 'src/types/audit';
import {
  LoanPlanType,
  LoanAgreementStatus,
  LoanInstallmentStatus,
  LoanInstallmentFrequency,
} from 'src/types/loan-agreement';

export function toCents(rupees: number | string | null | undefined): number {
  return Math.round(Number(rupees || 0) * 100);
}

/**
 * Regular users may only pick today and the two days before it; admins
 * may pick any past date. Mirrors the server rule.
 */
export function earliestAllowedDate(isAdmin: boolean): Date | undefined {
  return isAdmin ? undefined : subDays(startOfDay(new Date()), 2);
}

/**
 * Client-side preview of the equal-installment plan. The server builds the
 * real rows from the same inputs; addMonths(first, i) anchors on the first
 * date's day like the server does (31 Jan -> 28 Feb -> 31 Mar).
 */
export function previewEqualSchedule(
  capitalInCents: number,
  installmentAmountInCents: number,
  firstDueDate: Date | null,
  frequency: LoanInstallmentFrequency
): { dueDate: Date; amountInCents: number }[] {
  if (!firstDueDate || capitalInCents <= 0 || installmentAmountInCents <= 0) {
    return [];
  }
  if (installmentAmountInCents > capitalInCents) {
    return [];
  }

  const count = Math.ceil(capitalInCents / installmentAmountInCents);
  if (count > 520) {
    return [];
  }

  return Array.from({ length: count }, (_, i) => ({
    dueDate:
      frequency === LoanInstallmentFrequency.WEEKLY
        ? addWeeks(firstDueDate, i)
        : addMonths(firstDueDate, i),
    amountInCents:
      i === count - 1
        ? capitalInCents - installmentAmountInCents * (count - 1)
        : installmentAmountInCents,
  }));
}

export function planDescription(loan: {
  planType: LoanPlanType;
  frequency?: LoanInstallmentFrequency;
  installmentAmountInCents?: number;
  schedule: unknown[];
}): string {
  const count = loan.schedule.length;
  if (loan.planType === LoanPlanType.EQUAL) {
    return `Equal ${loan.frequency} installments of ${fCurrencyRupees(
      loan.installmentAmountInCents ?? 0
    )} (${count} installments)`;
  }
  return `Custom plan (${count} installment${count === 1 ? '' : 's'})`;
}

export const LOAN_STATUS_LABEL: Record<LoanAgreementStatus, { label: string; color: LabelColor }> =
  {
    [LoanAgreementStatus.ACTIVE]: { label: 'Active', color: 'info' },
    [LoanAgreementStatus.SETTLED]: { label: 'Settled', color: 'success' },
    [LoanAgreementStatus.BAD_DEBT]: { label: 'Bad Debt', color: 'error' },
  };

export const INSTALLMENT_STATUS_LABEL: Record<
  LoanInstallmentStatus,
  { label: string; color: LabelColor }
> = {
  [LoanInstallmentStatus.PAID]: { label: 'Paid', color: 'success' },
  [LoanInstallmentStatus.PARTIAL]: { label: 'Partly paid', color: 'info' },
  [LoanInstallmentStatus.OVERDUE]: { label: 'Overdue', color: 'warning' },
  [LoanInstallmentStatus.UPCOMING]: { label: 'Upcoming', color: 'default' },
};

/**
 * Human-readable line for a loan audit row. Shared by the agreement's
 * activity log and the employee activity log (which includes the
 * employee's loans).
 */
export function buildLoanActivityMessage(item: IAuditListItem): string | null {
  const { type, action, before, after } = item;

  if (type === AuditType.LOAN_AGREEMENT) {
    if (action === AuditAction.CREATED) {
      return `Loan agreement of ${fCurrencyRupees(after?.capitalInCents ?? 0)} created`;
    }
    if (action === AuditAction.DELETED) return 'Loan agreement deleted';

    if (after?.status === LoanAgreementStatus.BAD_DEBT && after?.badDebt?.reason) {
      return `Loan marked as bad debt — ${after.badDebt.reason}`;
    }
    if (before?.status === LoanAgreementStatus.BAD_DEBT && after?.badDebtReversal?.reason) {
      return `Bad debt mark reversed — ${after.badDebtReversal.reason}`;
    }
    if (after?.status === LoanAgreementStatus.SETTLED) return 'Loan settled';
    if (before?.status === LoanAgreementStatus.SETTLED) return 'Loan re-opened';
    if (after?.capitalInCents !== undefined) {
      return `Loan capital changed from ${fCurrencyRupees(
        before?.capitalInCents ?? 0
      )} to ${fCurrencyRupees(after.capitalInCents)} (plan re-entered)`;
    }

    const changedFields = Object.keys(after ?? {}).filter(
      (key) => !['status', 'settledAt', 'badDebtOverride'].includes(key)
    );
    return changedFields.length > 0
      ? `Loan agreement updated (${changedFields.join(', ')})`
      : 'Loan agreement updated';
  }

  if (type === AuditType.LOAN_PAYMENT) {
    if (action === AuditAction.CREATED) {
      return `Loan installment of ${fCurrencyRupees(after?.amountInCents ?? 0)} received`;
    }
    if (action === AuditAction.DELETED) {
      return `Loan installment of ${fCurrencyRupees(before?.amountInCents ?? 0)} deleted`;
    }
    if (after?.amountInCents !== undefined) {
      return `Loan installment changed from ${fCurrencyRupees(
        before?.amountInCents ?? 0
      )} to ${fCurrencyRupees(after.amountInCents)}`;
    }
    return 'Loan installment updated';
  }

  return null;
}
